/* eslint-disable prefer-spread */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-param-reassign */
import moment from 'moment';

class BaseDataTransformer {
  transformMultiple(data, options, isMultiLocationReport, locationIds) {
    let result = [];
    
    if (isMultiLocationReport) {

      // add übersicht, lokalId, lokalname columns to the rows for each location
      const newData = data.map(({ comparison, reportDataId, ...values}) => {
        return Object.values(values).map(({ value, locationId}) => ({
            comparison,
            reportDataId,
            data: value.data.map(d => ({ values: [locationId, locationIds[locationId].name, ...d.values]})),
            columns: ['LokalId', 'Lokalname', ...value.columns ],
          })
        );
      });

      data = newData.flat();


      // combine data of all locations into one entry
      data = [
        data.reduce((previousValue, currentValue) => ({
          ...currentValue,
          data: previousValue.data.concat(currentValue.data),
      }))];
    }

    data.forEach((d) => {
      let transformed = this.transform(d, options);
      if (d.comparison) {
        if (!options.locationCompare && Array.isArray(transformed)) {
          transformed = transformed.map((t) => {
            t.comparison = true;
            return t;
          });
        }
      }

      result.push(transformed);
    });

    if (result.length > 1 && options.calculateSum && result[0]) {
      const sums = [];
      for (let i = 0; i < result[0].length; i++) {
        let name = null;
        const sum = result.map((val) => {
          if (i >= val.length) {
            return 0;
          }
          name = val[i].name;
          return val[i].value;
        }).reduce((prev, cur) => prev + this.forceFloat(cur), 0);

        const isAverage = name.indexOf('Durchschnitt') >= 0;

        const prefix = isAverage ? 'Gesamt  Ø' : 'Summe';
        const value = isAverage ? sum / result.length : sum;
        sums.push({ value, name: `${prefix} ${name}` });
      }

      result.unshift(sums);
    }

    if (result.length > 1 && options.typeOfFirstColumn === 'date') {
      result = this.alignDateSeries(result);

      // align x values of all series
      for (let i = 0; i < result.length; i++) {
        for (let j = 0; j < result[i].data.length; j++) {
          const compareData = result[result.length - 1].data;
          result[i].data[j] = {
            name: result[i].data[j][0],
            x: compareData.length ? compareData[j][0] : result[i].data[j][0],
            y: result[i].data[j][1],
          };
        }
      }
    }

    return result;
  }

  gapForResolution(next, current, resolution = 'hour') {
    const diff = next.diff(current);
    const momentDuration = moment.duration(diff);

    switch (resolution) {
      case 'hour':
        return momentDuration.asHours();
      case 'day':
        return momentDuration.asDays();
      case 'week':
        return momentDuration.asWeeks();
      case 'month':
        return Math.floor(momentDuration.asMonths());
      case 'year':
        return momentDuration.asYears();
      default:
        throw new Error('invalid resolution');
    }
  }

  significantSegmentForResolution(date, resolution = 'hour') {
    const m = moment(date);

    switch (resolution) {
      case 'hour':
        return m.hour();
      case 'day':
        return m.date();
      case 'week':
        return m.week();
      case 'month':
        return m.month();
      case 'year':
        return m.year();
      default:
        throw new Error('invalid resolution');
    }
  }

  detectResolution(series) {
    const timestamps = series.data.map((d, i) => ((i < series.data.length - 1)
      ? series.data[i + 1][0] : Number.MAX_VALUE) - d[0]);

    const minOffset = Math.min.apply(Math, timestamps);

    if (minOffset < 864e5) {
      return 'hour';
    } if (minOffset < 864e5 * 7) {
      return 'day';
    } if (minOffset <= 864e5 * 7 * 31) {
      return 'month';
    }
    return 'month';
  }

  alignDateSeries(series) {
    if (Array.isArray(series[0])) {
      series = series.flat();
    }
    series = series.map(d => (d.series ? d.series : d));
    const significantSegmentForFirstValue = (s, resolution) => s.data[0]
      && this.significantSegmentForResolution(s.data[0][0], resolution);
    const firstValueTimes = series.map((s) => {
      const resolution = this.detectResolution(s);
      return significantSegmentForFirstValue(s, resolution);
    });

    const resolution = this.detectResolution(series[0]);

    const earliestValueTime = Math.min.apply(Math, firstValueTimes);

    series.forEach((s) => {
      if (!s.data.length) {
        return;
      }

      const r = this.detectResolution(s);

      if (r === 'hour') {
        // align hour on start
        const hoursToPrepend = significantSegmentForFirstValue(s, r) - earliestValueTime;
        for (let i = 0; i < hoursToPrepend; i++) {
          s.data.unshift([moment(s.data[0][0]).subtract(r, 1).valueOf(), 0]);
        }
      }


      for (let i = 0; i < s.data.length - 1; i++) {
        const nextVal = moment(s.data[i + 1][0]);
        const thisVal = moment(s.data[i][0]);
        // fill empty gaps
        const gapToNextValue = this.gapForResolution(nextVal, thisVal, resolution);

        if (gapToNextValue > 1) {
          for (let j = 1; j < gapToNextValue; j++) {
            s.data.splice(i + j, 0, [moment(s.data[i][0]).add(r, j).valueOf(), 0]);
          }
        }
      }
    });

    const lengths = series.map(a => a.data.length);
    const longestSeries = Math.max.apply(Math, lengths);
    series.forEach((s) => {
      const r = this.detectResolution(s);
      if (!s.data.length) {
        return;
      }
      // align hour on end
      const hoursToAppend = longestSeries - s.data.length;
      for (let i = 0; i < hoursToAppend; i++) {
        s.data.push([moment(s.data[s.data.length - 1][0]).add(r, 1), 0]);
      }
    });

    return series;
  }

  transform(data) {
    return data;
  }

  forceFloat(val) {
    if (!val) return 0;

    return Number(val.toString().replace(',', '.'));
  }

  resolveKeyPath(obj, keyPath) {
    keyPath = keyPath.replace(/\[(\w+)\]/g, '.$1');
    keyPath = keyPath.replace(/^\./, '');

    const components = keyPath.split('.');
    for (let i = 0, n = components.length; i < n; ++i) {
      const k = components[i];
      if (!(k in obj)) return null;

      obj = obj[k];
    }

    return obj;
  }
}

export default BaseDataTransformer;
